import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { useFloorPlanZones } from "@/hooks/useFloorPlanZones";
import { useFloorPlanPositions } from "@/hooks/useFloorPlanPositions";
import { supabase } from "@/integrations/supabase/client";
import { Table } from "@/types/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload,
  FileImage,
  FileText,
  X,
  ArrowLeft,
  ArrowRight,
  Check,
  Send,
  Loader2,
  UtensilsCrossed,
  MapPin,
  Link2,
  MessageSquare,
  Sparkles,
  Plus,
  Minus,
  Phone,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface FloorPlanSetupRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tables: Table[];
  onRefetchTables?: () => void;
}

interface TableSnapshot {
  id: string;
  name: string;
  capacity: number;
  isActive: boolean;
  isBookable: boolean;
  zoneName?: string;
}

interface ZoneSnapshot {
  id: string;
  name: string;
  color?: string;
}

interface FileWithPreview {
  file: File;
  preview: string | null;
  id: string;
}

type Step = "upload" | "tables" | "notes" | "confirm";

const STEPS: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: "upload", label: "Fichiers", icon: <Upload className="h-4 w-4" /> },
  { id: "tables", label: "Tables", icon: <UtensilsCrossed className="h-4 w-4" /> },
  { id: "notes", label: "Notes", icon: <MessageSquare className="h-4 w-4" /> },
  { id: "confirm", label: "Envoi", icon: <Send className="h-4 w-4" /> },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

export function FloorPlanSetupRequestDialog({
  open,
  onOpenChange,
  tables,
  onRefetchTables,
}: FloorPlanSetupRequestDialogProps) {
  const { user, establishmentId } = useAuth();
  const { currentEstablishment } = useEstablishment();
  const { zones } = useFloorPlanZones({ establishmentId: establishmentId || undefined });
  const { positions } = useFloorPlanPositions(tables, [], { establishmentId: establishmentId || undefined });

  const [currentStep, setCurrentStep] = useState<Step>("upload");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form state - multiple files
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [combinationNotes, setCombinationNotes] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [callPreference, setCallPreference] = useState("");
  const [positioningNotes, setPositioningNotes] = useState("");

  // Table editing state
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editCapacity, setEditCapacity] = useState("");

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setCurrentStep("upload");
      setFiles([]);
      setCombinationNotes("");
      setPositioningNotes("");
      setContactPhone("");
      setCallPreference("");
      setUploadProgress(0);
      setEditingTableId(null);
    }
  }, [open]);

  // Get zone name for a table
  const getTableZoneName = useCallback((tableId: string): string | undefined => {
    const position = positions.find(p => p.tableId === tableId);
    if (!position?.zoneId) return undefined;
    const zone = zones.find(z => z.id === position.zoneId);
    return zone?.name;
  }, [positions, zones]);

  // Build table snapshots
  const tablesSnapshot: TableSnapshot[] = tables
    .filter(t => t.isActive)
    .map(t => ({
      id: t.id,
      name: t.name,
      capacity: t.capacity,
      isActive: t.isActive,
      isBookable: t.isBookable,
      zoneName: getTableZoneName(t.id),
    }));

  const zonesSnapshot: ZoneSnapshot[] = zones.map(z => ({
    id: z.id,
    name: z.name,
    color: z.color || undefined,
  }));

  // File handling - multiple files
  const addFiles = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const remainingSlots = MAX_FILES - files.length;
    
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_FILES} fichiers autorisés`);
      return;
    }

    const filesToAdd = fileArray.slice(0, remainingSlots);
    const validFiles: FileWithPreview[] = [];

    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} est trop volumineux (max 10MB)`);
        continue;
      }

      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name} : format non supporté. Utilisez PDF, JPG ou PNG.`);
        continue;
      }

      const fileWithPreview: FileWithPreview = {
        file,
        preview: null,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };

      // Generate preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles(prev => prev.map(f => 
            f.id === fileWithPreview.id 
              ? { ...f, preview: e.target?.result as string }
              : f
          ));
        };
        reader.readAsDataURL(file);
      }

      validFiles.push(fileWithPreview);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    if (fileArray.length > remainingSlots) {
      toast.warning(`Seulement ${remainingSlots} fichier(s) ajouté(s). Maximum ${MAX_FILES} au total.`);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    addFiles(selectedFiles);
    // Reset input value to allow selecting same file again
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Table inline editing
  const startEditingTable = (table: Table) => {
    setEditingTableId(table.id);
    setEditName(table.name);
    setEditCapacity(table.capacity.toString());
  };

  const saveTableEdit = async () => {
    if (!editingTableId) return;

    try {
      const { error } = await supabase
        .from("tables")
        .update({
          name: editName,
          capacity: parseInt(editCapacity, 10),
        })
        .eq("id", editingTableId);

      if (error) throw error;

      setEditingTableId(null);
      onRefetchTables?.();
      toast.success("Table mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const cancelTableEdit = () => {
    setEditingTableId(null);
    setEditName("");
    setEditCapacity("");
  };

  // Navigation
  const canProceed = (): boolean => {
    switch (currentStep) {
      case "upload":
        return files.length > 0;
      case "tables":
        return tablesSnapshot.length > 0;
      case "notes":
        return true;
      case "confirm":
        return true;
      default:
        return false;
    }
  };

  const goToNextStep = () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[stepIndex + 1].id);
    }
  };

  const goToPreviousStep = () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex > 0) {
      setCurrentStep(STEPS[stepIndex - 1].id);
    }
  };

  // Submit request
  const handleSubmit = async () => {
    if (files.length === 0 || !user || !establishmentId || !currentEstablishment) return;

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      // 1. Upload all files to storage
      const uploadedFiles: { path: string; name: string }[] = [];
      const progressPerFile = 50 / files.length;

      for (let i = 0; i < files.length; i++) {
        const fileWithPreview = files[i];
        const fileExt = fileWithPreview.file.name.split(".").pop();
        const filePath = `${user.id}/${establishmentId}/${Date.now()}-${i}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("floor-plan-requests")
          .upload(filePath, fileWithPreview.file);

        if (uploadError) throw uploadError;

        uploadedFiles.push({ path: filePath, name: fileWithPreview.file.name });
        setUploadProgress((i + 1) * progressPerFile);
      }

      setUploadProgress(60);

      // 2. Send request via edge function with arrays
      const { data, error } = await supabase.functions.invoke("send-floor-plan-request", {
        body: {
          establishmentId,
          establishmentName: currentEstablishment.name,
          // Send both old format (for backward compatibility) and new format
          filePath: uploadedFiles[0]?.path,
          fileName: uploadedFiles[0]?.name,
          filePaths: uploadedFiles.map(f => f.path),
          fileNames: uploadedFiles.map(f => f.name),
          tablesSnapshot,
          zonesSnapshot,
          combinationNotes: combinationNotes || undefined,
          positioningNotes: positioningNotes || undefined,
          contactPhone: contactPhone || undefined,
          callPreference: callPreference || undefined,
        },
      });

      if (error) throw error;

      setUploadProgress(100);

      toast.success("Demande envoyée !", {
        description: "Notre équipe configurera votre plan de salle dans les 12 heures ouvrées.",
      });

      onOpenChange(false);
    } catch (error: any) {
      console.error("Error submitting floor plan request:", error);
      toast.error("Erreur lors de l'envoi", {
        description: error.message || "Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);
  const progressPercentage = ((stepIndex + 1) / STEPS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Assistance configuration du plan de salle
          </DialogTitle>
          <DialogDescription>
            Envoyez-nous vos plans et nous les configurons pour vous sous 12 heures ouvrées.
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 py-2">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={cn(
                "flex items-center gap-1.5 text-sm",
                index <= stepIndex ? "text-primary" : "text-muted-foreground"
              )}
            >
              <div
                className={cn(
                  "flex items-center justify-center w-7 h-7 rounded-full border-2 transition-colors",
                  index < stepIndex && "bg-primary border-primary text-primary-foreground",
                  index === stepIndex && "border-primary text-primary",
                  index > stepIndex && "border-muted-foreground/30"
                )}
              >
                {index < stepIndex ? <Check className="h-4 w-4" /> : step.icon}
              </div>
              <span className="hidden sm:inline font-medium">{step.label}</span>
              {index < STEPS.length - 1 && (
                <div className="w-8 h-0.5 bg-muted-foreground/20 mx-1" />
              )}
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <ScrollArea className="h-full pr-4">
          {/* Step 1: Upload - Multiple Files */}
          {currentStep === "upload" && (
            <div className="space-y-4 py-4">
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className={cn(
                  "border-2 border-dashed rounded-lg p-8 text-center transition-colors relative",
                  "hover:border-primary/50 hover:bg-primary/5",
                  files.length > 0 ? "border-primary bg-primary/5" : "border-muted-foreground/25"
                )}
              >
                <div className="space-y-4">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="font-medium">Glissez vos plans de salle ici</p>
                    <p className="text-sm text-muted-foreground">
                      ou cliquez pour sélectionner des fichiers
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PDF, JPG ou PNG • Max {MAX_FILES} fichiers • 10 Mo par fichier
                  </p>
                </div>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
                />
              </div>

              {/* Files list */}
              {files.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      Fichiers ajoutés ({files.length}/{MAX_FILES})
                    </p>
                    {files.length < MAX_FILES && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="relative"
                        onClick={() => document.getElementById('add-more-files')?.click()}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Ajouter
                        <Input
                          id="add-more-files"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          multiple
                          onChange={handleFileChange}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {files.map((fileWithPreview) => (
                      <div
                        key={fileWithPreview.id}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                      >
                        {fileWithPreview.preview ? (
                          <img
                            src={fileWithPreview.preview}
                            alt="Aperçu"
                            className="h-12 w-12 rounded object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                            <FileText className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {fileWithPreview.file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(fileWithPreview.file.size / 1024 / 1024).toFixed(2)} Mo
                          </p>
                        </div>
                        <Badge variant="secondary" className="gap-1 shrink-0">
                          {fileWithPreview.file.type.includes("pdf") ? (
                            <FileText className="h-3 w-3" />
                          ) : (
                            <FileImage className="h-3 w-3" />
                          )}
                          {fileWithPreview.file.type.includes("pdf") ? "PDF" : "Image"}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          onClick={() => removeFile(fileWithPreview.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Tables */}
          {currentStep === "tables" && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Vos tables ({tablesSnapshot.length})</h3>
                  <p className="text-sm text-muted-foreground">
                    Vérifiez et modifiez si nécessaire
                  </p>
                </div>
              </div>

              {tablesSnapshot.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UtensilsCrossed className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune table active configurée</p>
                  <p className="text-sm mb-4">
                    Ajoutez d'abord des tables pour continuer
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    asChild
                  >
                    <Link to="/dashboard/tables">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter des tables
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {tablesSnapshot.map((table) => {
                    const originalTable = tables.find(t => t.id === table.id);
                    const isEditing = editingTableId === table.id;

                    return (
                      <div
                        key={table.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                          isEditing ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                        )}
                      >
                        {isEditing ? (
                          <>
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="flex-1 h-8"
                              placeholder="Nom"
                            />
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                onClick={() => setEditCapacity(String(Math.max(1, parseInt(editCapacity) - 1)))}
                                disabled={parseInt(editCapacity) <= 1}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="text-lg font-semibold min-w-[2rem] text-center">{editCapacity}</span>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                onClick={() => setEditCapacity(String(parseInt(editCapacity) + 1))}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button size="sm" variant="ghost" onClick={saveTableEdit}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelTableEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1">
                              <p className="font-medium">{table.name}</p>
                              {table.zoneName && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {table.zoneName}
                                </p>
                              )}
                            </div>
                            <Badge variant="outline">{table.capacity} pers.</Badge>
                            {table.isBookable ? (
                              <Badge variant="secondary" className="text-xs">
                                Réservable
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-muted-foreground">
                                Non réservable
                              </Badge>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => originalTable && startEditingTable(originalTable)}
                            >
                              Modifier
                            </Button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {zones.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Zones configurées</h4>
                  <div className="flex flex-wrap gap-2">
                    {zones.map((zone) => (
                      <Badge
                        key={zone.id}
                        variant="outline"
                        style={{
                          borderColor: zone.color || undefined,
                          backgroundColor: zone.color ? `${zone.color}20` : undefined,
                        }}
                      >
                        {zone.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Notes */}
          {currentStep === "notes" && (
            <div className="space-y-6 py-4">
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  Combinaisons de tables (optionnel)
                </Label>
                <Textarea
                  value={combinationNotes}
                  onChange={(e) => setCombinationNotes(e.target.value)}
                  placeholder="Exemple :&#10;Table 1 + Table 2 peuvent être jointes (6 personnes)&#10;Tables T1, T2, T3 peuvent former une grande table"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Indiquez quelles tables peuvent être combinées ensemble
                </p>
              </div>

              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Notes de positionnement (optionnel)
                </Label>
                <Textarea
                  value={positioningNotes}
                  onChange={(e) => setPositioningNotes(e.target.value)}
                  placeholder="Exemple :&#10;La table 3 est près de la fenêtre&#10;Le bar est à gauche en entrant&#10;La terrasse est couverte"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Ajoutez des informations pour nous aider à positionner vos tables
                </p>
              </div>

              {/* Contact phone section */}
              <div className="pt-4 border-t space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>Besoin de clarifier certains points ?</span>
                </div>
                
                <div className="space-y-3">
                  <Label>Numéro de téléphone (optionnel)</Label>
                  <Input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="+41 79 123 45 67"
                  />
                  <p className="text-xs text-muted-foreground">
                    Si vous souhaitez être contacté par téléphone pour des précisions
                  </p>
                </div>
                
                {contactPhone && (
                  <div className="space-y-3">
                    <Label>Préférence de disponibilité (optionnel)</Label>
                    <Input
                      value={callPreference}
                      onChange={(e) => setCallPreference(e.target.value)}
                      placeholder="Ex: En semaine de 10h à 18h, pas le lundi matin..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Indiquez quand nous pouvons vous appeler
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {currentStep === "confirm" && (
            <div className="space-y-6 py-4">
              <div className="rounded-lg border p-4 space-y-4">
                <h3 className="font-medium">Résumé de votre demande</h3>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <p className="text-muted-foreground">Fichiers</p>
                    <p className="font-medium flex items-center gap-1">
                      <FileImage className="h-4 w-4" />
                      {files.length} fichier(s)
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-muted-foreground">Tables</p>
                    <p className="font-medium">{tablesSnapshot.length} table(s) active(s)</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-muted-foreground">Zones</p>
                    <p className="font-medium">{zones.length} zone(s)</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-muted-foreground">Délai estimé</p>
                    <p className="font-medium text-primary">12 heures ouvrées</p>
                  </div>
                </div>

                {/* List uploaded files */}
                {files.length > 0 && (
                  <div className="pt-3 border-t text-sm">
                    <p className="text-muted-foreground mb-2">Fichiers joints :</p>
                    <ul className="space-y-1">
                      {files.map((f, i) => (
                        <li key={f.id} className="flex items-center gap-2">
                          {f.file.type.includes("pdf") ? (
                            <FileText className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <FileImage className="h-3 w-3 text-muted-foreground" />
                          )}
                          <span className="truncate">{f.file.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(combinationNotes || positioningNotes || contactPhone) && (
                  <div className="pt-3 border-t text-sm">
                    <p className="text-muted-foreground mb-1">Notes ajoutées :</p>
                    {combinationNotes && <p>• Combinaisons de tables</p>}
                    {positioningNotes && <p>• Notes de positionnement</p>}
                    {contactPhone && <p>• Téléphone : {contactPhone}</p>}
                  </div>
                )}
              </div>

              <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                <p className="text-sm">
                  <strong>Que va-t-il se passer ?</strong>
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li>• Nous recevrons vos plans et vos informations</li>
                  <li>• Notre équipe positionnera vos tables sur le plan</li>
                  <li>• Vous recevrez un email de confirmation une fois terminé</li>
                </ul>
              </div>

              {isSubmitting && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} />
                  <p className="text-sm text-center text-muted-foreground">
                    {uploadProgress < 50 ? "Envoi des fichiers..." : "Finalisation..."}
                  </p>
                </div>
              )}
            </div>
          )}
          </ScrollArea>
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="ghost"
            onClick={goToPreviousStep}
            disabled={stepIndex === 0 || isSubmitting}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>

          {currentStep === "confirm" ? (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer la demande
                </>
              )}
            </Button>
          ) : (
            <Button onClick={goToNextStep} disabled={!canProceed()}>
              Continuer
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
